import { Link } from 'react-router-dom';

/**
 * The funnel chart, shared by the manager and consultant dashboards.
 *
 * There were two copies of this before, differing only in a few pixel values,
 * and they had already drifted — one showed a conversion column, the other
 * silently dropped it. One component, one `compact` flag.
 *
 * ── It is a funnel again ──
 * The previous pass replaced the silhouette with left-aligned bars, on the
 * argument that a centred shape costs precision. That was half right and it
 * threw away the thing the card is for: a funnel is a shape you read at a
 * glance — where it pinches is the problem — and a stack of bars is not.
 *
 * So the shape is back, and it is a real funnel: each stage is a trapezoid
 * running from its own width to the next stage's, so consecutive bands join
 * into one continuous silhouette instead of a stack of separate rectangles.
 * The taper between two rows IS the drop-off between them.
 *
 * What does not come back is the dishonesty. Width is share of intake with no
 * minimum, so a stage that nobody reached pinches to nothing rather than
 * drawing the old `max(10%, …)` floor — which is why an empty funnel used to
 * look like a working chart with small numbers in it. And the geometry is
 * data-driven: the legacy dashboard stepped every stage down by a fixed amount
 * "so the shape is always a funnel", which makes the silhouette a decoration
 * that cannot be wrong. This one can be wrong, which is the point of drawing it.
 *
 * Numbers stay OUTSIDE the shape, in their own columns. White text with a drop
 * shadow, printed on top of a band that could be any width, fails contrast
 * exactly when the band is narrow — which is when the number matters most.
 *
 * It is still a real <table> with a caption and column headers, so the two
 * percentage columns are named. They are different numbers — step-to-step and
 * share of intake — and the original chart showed one of them unlabelled.
 */
export default function FunnelChart({
  rows,
  periodLabel = '',
  compact = false,
  widen = null,
  caption = 'Pipeline stages',
}) {
  if (!rows) {
    return <div className="mono text-xs text-muted">Needs the role-snapshot endpoint.</div>;
  }

  // The honest empty state. This is the fix for a funnel of zeroes sitting
  // under an exception list full of leads: the leads are real, they are just
  // older than the window. Saying that — and offering the wider window — is the
  // whole content of the card when there is no data in it.
  if (rows.isEmpty) {
    return (
      <div className="empty-state" style={{ padding: '20px 12px', textAlign: 'center' }}>
        <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
          No leads moved through the pipeline in the last {periodLabel || 'period'}.
        </div>
        <div className="mono text-xs text-muted" style={{ marginTop: 6 }}>
          Older leads are still open — they just did not enter or advance in this window.
        </div>
        <div style={{ marginTop: 12, display: 'flex', gap: 8, justifyContent: 'center' }}>
          {widen && (
            <button type="button" className="btn small" onClick={widen.apply}>
              Show {widen.label}
            </button>
          )}
          <Link to="/leads" className="btn small">Open pipeline</Link>
        </div>
      </div>
    );
  }

  const rowH = compact ? 26 : 34;

  return (
    <div className="table-scroll">
      <table className="data-table funnel-table" style={{ width: '100%' }}>
        <caption className="sr-only">
          {caption}{periodLabel ? ` over the last ${periodLabel}` : ''}. Counts are leads that
          reached each stage.
        </caption>
        <thead>
          <tr>
            <th style={{ textAlign: 'left' }}>Stage</th>
            <th style={{ width: '42%' }} aria-hidden="true" />
            <th style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>Leads</th>
            {/* Step / Of intake percentage columns hidden by request (all logins).
            <th style={{ textAlign: 'right', whiteSpace: 'nowrap' }} title="Conversion from the stage above">Step</th>
            {!compact && (
              <th style={{ textAlign: 'right', whiteSpace: 'nowrap' }} title="Conversion from Inbound">Of intake</th>
            )}
            */}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={r.key}>
              <td style={{ whiteSpace: 'nowrap', height: rowH }}>
                <div style={{ fontSize: 13, color: 'var(--text-secondary)', fontWeight: r.key === 'CONFIRMED' ? 600 : 400 }}>
                  {r.label}
                </div>
                {/* Annotations, not stages. See lib/funnelStages.js. */}
                {r.sub && !compact && (
                  <div className="mono text-xs text-muted" style={{ marginTop: 1 }}>{r.sub}</div>
                )}
              </td>
              {/* No padding and no bottom border: these cells have to touch for
                  the bands to read as one shape. position:relative + an
                  absolutely-positioned SVG lets the band fill the row whatever
                  height the label beside it turns out to be. */}
              <td style={{ position: 'relative', padding: 0, borderBottom: 'none' }}>
                <FunnelBand
                  top={r.width}
                  bottom={i === rows.length - 1 ? r.width : rows[i + 1].width}
                  color={r.color}
                  empty={r.count === 0}
                />
              </td>
              <td className="mono" style={{ textAlign: 'right', fontWeight: 600, color: r.count === 0 ? 'var(--text-tertiary)' : 'var(--text-primary)' }}>
                {r.count}
              </td>
              {/* Step / Of intake percentage cells hidden by request (all logins).
              <td className="mono text-xs" style={{ textAlign: 'right', color: dropTone(r.conv) }}>
                {r.conv == null ? '—' : `${r.conv}%`}
              </td>
              {!compact && (
                <td className="mono text-xs" style={{ textAlign: 'right', color: 'var(--text-tertiary)' }}>
                  {r.ofIntake == null ? '—' : `${r.ofIntake}%`}
                </td>
              )}
              */}
            </tr>
          ))}
        </tbody>
      </table>

      {/* The All/New/Used tabs do not reconcile unless this is said out loud.
          See lib/funnelStages.js. */}
      {rows.unclassified > 0 && (
        <div className="mono text-xs text-muted" style={{ marginTop: 6 }}>
          {rows.unclassified} of {rows.intake} not classified New or Used
          {rows.demo > 0 ? ` (${rows.demo} demo)` : ''} — counted in All only.
        </div>
      )}
      <Link to="/leads" className="mono text-xs" style={{ display: 'inline-block', marginTop: 6, color: 'var(--accent-primary-hover)', textDecoration: 'none' }}>
        open pipeline →
      </Link>
    </div>
  );
}

/**
 * One band of the funnel: a trapezoid from `top` width to `bottom` width, both
 * 0..1 and both centred, drawn edge-to-edge so it meets the bands above and
 * below without a seam.
 *
 * preserveAspectRatio="none" with a 0..100 x-axis means x is literally a
 * percentage and the band stretches to whatever height the row ends up being —
 * so a row carrying an annotation line does not tear the silhouette.
 *
 * A non-zero stage keeps a hairline minimum so it stays visible; a zero stage
 * gets nothing at all, and the funnel closes to a point. That is the shape
 * telling the truth, and it is the whole reason to draw one.
 */
function FunnelBand({ top, bottom, color, empty }) {
  const MIN = 1.2;             // percent — a hairline, not the old 10% floor
  const w = (v) => (v <= 0 ? 0 : Math.max(MIN, Math.min(100, v * 100)));
  const t = w(top);
  const b = w(bottom);
  if (t === 0 && b === 0) return null;

  // A hairline gap top and bottom so consecutive bands read as separate
  // stages rather than one undifferentiated taper — the segmented funnel every
  // dealer has seen before — while the slope between them still carries the
  // drop-off. The gap is sub-pixel-ish at real row heights, so it separates
  // without breaking the silhouette.
  const G = 0.3;
  const pts = [
    [50 - t / 2, G], [50 + t / 2, G],
    [50 + b / 2, 10 - G], [50 - b / 2, 10 - G],
  ].map((p) => p.join(',')).join(' ');

  return (
    <svg
      viewBox="0 0 100 10"
      preserveAspectRatio="none"
      aria-hidden="true"
      style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', display: 'block' }}
    >
      <polygon points={pts} fill={color} opacity={empty ? 0.25 : 1} />
    </svg>
  );
}

/**
 * The step column is where a manager finds the leak, so a bad step should not
 * look like every other number. Thresholds are deliberately loose — this is a
 * hint to look, not a judgement, and a dealership's real conversion norms are
 * not something the portal knows yet.
 */
// eslint-disable-next-line no-unused-vars -- only used by the hidden Step column
function dropTone(conv) {
  if (conv == null) return 'var(--text-tertiary)';
  if (conv < 25) return 'var(--danger)';
  if (conv < 50) return 'var(--warning)';
  return 'var(--text-secondary)';
}
